import { Box, IconButton, Tooltip } from "@mui/material";
import { Description, Edit, Email as EmailIcon, Print as PrintIcon, Delete as DeleteIcon } from "@mui/icons-material";
import React, { useEffect, useState, useCallback } from "react";
import PageRequest from "../../core/api/PageRequest";
import PageResponse from "../../core/api/PageResponse";
import InvoiceModalView from "./InvoiceModalView";
import InvoiceEmailModalForm from "../email/InvoiceEmailModalForm";
import InvoicePrintComponent from "./InvoicePrintComponent";
import InvoiceEditModalForm from "./InvoceEditModalForm";
import InvoiceModel, { InvoiceData } from "../../core/api/invoice/InvoiceModel";
import { useNotifications } from "../../hooks/NotificationProvider";
import { translations } from "../../translations/common";
import { DataGrid, GridColDef, GridRenderCellParams, GridPaginationModel } from "@mui/x-data-grid";
import { tableContainerStyles, tableHeaderStyles, dataGridStyles } from "../common/styles/TableStyles";
import { confirmDialog } from "../common/ConfirmDialog";

export interface InvoiceTableProps {
  clientId?: string;
  fetchFunction: (page: PageRequest) => Promise<PageResponse<InvoiceData>>;
  tableRef: React.RefObject<any>;
}

interface InvoiceState {
  clientId: string;
  invoiceNo: string;
}

interface State {
  invoice?: InvoiceState;
  showModal: boolean;
  showPreview: boolean;
  showEmail: boolean;
  print: boolean;
}

const InvoiceTable: React.FC<InvoiceTableProps> = (props) => {
  const { tableRef, fetchFunction } = props;
  const notifications = useNotifications();
  const [state, setState] = useState<State>({
    showModal: false,
    showPreview: false,
    showEmail: false,
    print: false,
  });
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    pageSize: 10,
    page: 0,
  });
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<InvoiceData[]>([]);
  const [totalRows, setTotalRows] = useState(0);

  const closeEmailModal = () => {
    setState({
      ...state,
      showEmail: false,
      invoice: undefined,
    });
  };

  const closePreview = () => {
    setState({
      ...state,
      showPreview: false,
      invoice: undefined,
    });
  };

  const closeEdit = () => {
    setState({
      ...state,
      showModal: false,
      invoice: undefined,
    });
  };

  const closePrint = () => {
    setState({
      ...state,
      print: false,
      invoice: undefined,
    });
  };

  const handleDelete = async (invoiceNo: string) => {
    try {
      await InvoiceModel.delete(invoiceNo);
      fetchData();
    } catch (err: any) {
      notifications.notify({
        level: "error",
        message: err.response?.data?.description || "Error deleting invoice",
      });
    }
  };

  const columns: GridColDef[] = [
    {
      field: "clientId",
      headerName: translations.invoice.table.columns.clientId,
      flex: 1,
      maxWidth: 140,
      minWidth: 80,
    },
    {
      field: "date",
      headerName: translations.invoice.table.columns.date,
      flex: 1,
      minWidth: 100,
      maxWidth: 100,
    },
    {
      field: "title",
      headerName: translations.invoice.table.columns.invoiceNo,
      flex: 1,
      minWidth: 100,
      maxWidth: 100,
    },
    {
      field: "companyTitle",
      headerName: translations.invoice.table.columns.companyTitle,
      flex: 1,
      minWidth: 120,
    },
    {
      field: "periodFrom",
      headerName: translations.invoice.table.columns.periodFrom,
      flex: 1,
      minWidth: 120,
      maxWidth: 120,
    },
    {
      field: "periodTo",
      headerName: translations.invoice.table.columns.periodTo,
      flex: 1,
      minWidth: 120,
    },
    {
      field: "price",
      headerName: translations.invoice.table.columns.price,
      flex: 1,
      minWidth: 60,
      maxWidth: 60,
    },
    {
      field: "pvm",
      headerName: translations.invoice.table.columns.pvm,
      flex: 1,
      minWidth: 60,
      maxWidth: 60,
    },
    {
      field: "discount",
      headerName: translations.invoice.table.columns.discount,
      flex: 1,
      maxWidth: 80,
      minWidth: 80,
    },
    {
      field: "totalCount",
      headerName: translations.invoice.table.columns.totalCount,
      flex: 1,
      maxWidth: 100,
    },
    {
      field: "lastSentDate",
      headerName: translations.invoice.table.columns.lastSentDate,
      flex: 1,
      minWidth: 100,
    },
    {
      field: "author",
      headerName: translations.invoice.table.columns.author,
      flex: 1,
      minWidth: 80,
    },
    {
      field: "actions",
      headerName: "",
      flex: 1,
      sortable: false,
      minWidth: 150,
      renderCell: (params: GridRenderCellParams<InvoiceData>) => {
        const invoice = params.row;
        return (
          <Box sx={{ display: "flex" }}>
            <Tooltip title={translations.invoice.table.actions.print}>
              <IconButton
                onClick={() => {
                  setState({
                    ...state,
                    print: true,
                    invoice: {
                      clientId: invoice.clientId,
                      invoiceNo: invoice.invoiceNo,
                    },
                  });
                }}
                size="small"
              >
                <PrintIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title={translations.invoice.table.actions.edit}>
              <IconButton
                onClick={() => {
                  setState({
                    ...state,
                    print: false,
                    showModal: true,
                    invoice: {
                      clientId: invoice.clientId,
                      invoiceNo: invoice.invoiceNo,
                    },
                  });
                }}
                size="small"
              >
                <Edit />
              </IconButton>
            </Tooltip>
            <Tooltip title={translations.invoice.table.actions.view}>
              <IconButton
                onClick={() => {
                  setState({
                    ...state,
                    print: false,
                    showPreview: true,
                    invoice: {
                      clientId: invoice.clientId,
                      invoiceNo: invoice.invoiceNo,
                    },
                  });
                }}
                size="small"
              >
                <Description />
              </IconButton>
            </Tooltip>
            <Tooltip title={translations.invoice.table.actions.send}>
              <IconButton
                onClick={() => {
                  setState({
                    ...state,
                    print: false,
                    showEmail: true,
                    invoice: {
                      clientId: invoice.clientId,
                      invoiceNo: invoice.invoiceNo,
                    },
                  });
                }}
                size="small"
              >
                <EmailIcon />
              </IconButton>
            </Tooltip>
            {invoice.isLast && (
              <Tooltip title={translations.invoice.table.actions.delete}>
                <IconButton
                  onClick={() => {
                    confirmDialog({
                      title: "Ištrinti sąskaitą?",
                      body: `Ar tikrai norite ištrinti sąskaitą ${invoice.invoiceNo}?`,
                      action: async () => {
                        try {
                          await handleDelete(invoice.invoiceNo);
                          notifications.notify({
                            level: "success",
                            message: "Sąskaita sėkmingai ištrinta",
                          });
                        } catch (err: any) {
                          notifications.notify({
                            level: "error",
                            message: err.response?.data?.description || "Įvyko klaida trinant sąskaitą",
                          });
                        }
                      },
                      actionName: "Ištrinti",
                    });
                  }}
                  size="small"
                  color="error"
                >
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        );
      },
    },
  ];

  const fetchData = useCallback(() => {
    setLoading(true);
    fetchFunction({
      page: paginationModel.page,
      size: paginationModel.pageSize,
    })
      .then((result) => {
        // Add id property to each row for DataGrid
        const rowsWithIds = result.content.map((row) => ({
          ...row,
          id: row.invoiceNo || `invoice-${row.clientId}-${row.date}`,
        }));
        setRows(rowsWithIds);
        setTotalRows(result.page.totalElements);
      })
      .finally(() => setLoading(false));
  }, [paginationModel.page, paginationModel.pageSize, fetchFunction]);

  // Set up the tableRef once on component mount
  useEffect(() => {
    // Add onQueryChange method to the ref to allow external refresh calls
    if (tableRef.current) {
      tableRef.current.onQueryChange = fetchData;
    } else {
      tableRef.current = { onQueryChange: fetchData };
    }

    // Cleanup function to handle component unmounting
    return () => {
      if (tableRef.current) {
        tableRef.current.onQueryChange = () => console.warn('Table has been unmounted');
      }
    };
  }, [tableRef, fetchData]);

  // Separate effect to actually fetch data
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <Box sx={tableContainerStyles}>
      <Box sx={tableHeaderStyles}>
        {translations.invoice.table.title}
      </Box>
      <DataGrid
        rows={rows}
        columns={columns.map(col => ({ ...col, sortable: false }))}
        rowCount={totalRows}
        loading={loading}
        pageSizeOptions={[10]}
        paginationModel={paginationModel}
        paginationMode="server"
        onPaginationModelChange={setPaginationModel}
        disableColumnMenu
        disableRowSelectionOnClick
        getRowHeight={() => "auto"}
        slotProps={{
          pagination: {
            showFirstButton: true,
            showLastButton: true,
          },
        }}
        slots={{
          noRowsOverlay: () => (
            <Box sx={{ p: 2, textAlign: "center" }}>
              {translations.invoice.table.noInvoices}
            </Box>
          ),

        }}
        sx={dataGridStyles}
      />
      {state.invoice && state.showModal && (
        <InvoiceEditModalForm
          close={closeEdit}
          invoiceId={state.invoice.invoiceNo}
          tableRef={tableRef}
          onSuccess={() => {
            setState({
              ...state,
              showPreview: false,
              showModal: false,
              showEmail: true,
              print: false,
            });
          }}
        />
      )}

      {state.invoice && state.showPreview && (
        <InvoiceModalView
          close={closePreview}
          pdfUrl={() => InvoiceModel.getPdf(state.invoice?.invoiceNo || '')}
          sendAction={() => {
            setState({
              ...state,
              showPreview: false,
              showEmail: true,
            });
          }}
        />
      )}

      {state.invoice && state.showEmail && (
        <InvoiceEmailModalForm
          clientId={state.invoice.clientId}
          title={translations.clientControls.sendInvoice.title(state.invoice.invoiceNo)}
          close={closeEmailModal}
          pdfUrl={() => InvoiceModel.getPdf(state.invoice?.invoiceNo || '')}
          onSubmit={async (content) => {
            const invoiceNo = state.invoice?.invoiceNo;
            if (invoiceNo) {
              return await InvoiceModel.send(content, invoiceNo);
            }
            return Promise.reject("Invoice not present!");
          }}
        />
      )}

      {state.invoice && state.print && (
        <InvoicePrintComponent
          close={closePrint}
          pdfUrl={() => InvoiceModel.getPdf(state.invoice?.invoiceNo || '')}
        />
      )}
    </Box>
  );
};

export default InvoiceTable;
